import { useCallback } from 'react';
import * as authService from '@/services/authService';
import { useAuthStore, UserInfo } from '@/stores/authStore';
/**
 * useAuth Hook
 * 管理用户认证状态（使用 Zustand store）
 */
export function useAuth() {
  const {
    setIsLoggedIn,
    setUserInfo,
    setLoginUrl,
    setIsAuthInitializing,
    setAuthPromise,
    logout: logoutStore,
  } = useAuthStore();
  /**
   * 初始化登录状态
   */
  const initializeAuth = useCallback(async () => {
    // 使用 getState() 获取最新状态，避免闭包问题
    const currentState = useAuthStore.getState();
    // 如果有正在进行的认证 Promise，直接返回它（等待第一次完成）
    if (currentState.authPromise) {
      return currentState.authPromise;
    }
    setIsAuthInitializing(true);
    // 创建认证 Promise 并存储
    const authPromise = (async () => {
      try {
        const { isLoggedIn: loggedIn, userInfo: info } =
          await authService.initializeLoginState();
        setIsLoggedIn(loggedIn);
        setUserInfo((info as UserInfo) || null);
        return { isLoggedIn: loggedIn };
      } catch (error) {
        console.error('❌ Failed to initialize auth:', error);
        return { isLoggedIn: false };
      } finally {
        setIsAuthInitializing(false);
        setAuthPromise(null);
      }
    })();
    // 存储 Promise 以便后续调用可以等待
    setAuthPromise(authPromise);
    return authPromise;
  }, [setIsLoggedIn, setUserInfo, setIsAuthInitializing, setAuthPromise]);
  /**
   * 处理登录成功
   */
  const handleLoginSuccess = useCallback(async () => {
    // Set login state
    setIsLoggedIn(true);
    // Fetch user info
    try {
      const userInfo = await authService.fetchUserInfo();
      setUserInfo(userInfo as UserInfo);
      localStorage.setItem('user-info', JSON.stringify(userInfo));
    } catch (error) {
      console.error('Error fetching user info:', error);
      // Fall back to default user info
      const defaultUser: UserInfo = {
        name: 'User',
        email: '',
        homedir: '',
        hostname: '',
        platform: '',
        release: '',
        username: 'User',
      };
      setUserInfo(defaultUser);
      localStorage.setItem('user-info', JSON.stringify(defaultUser));
    }
  }, [setIsLoggedIn, setUserInfo]);
  /**
   * 登出
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      logoutStore();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }, [logoutStore]);
  /**
   * 处理未授权错误
   */
  const handleUnauthorized = useCallback(
    (redirectUrl?: string) => {
      logoutStore();
      setLoginUrl(redirectUrl || '');
    },
    [logoutStore, setLoginUrl]
  );
  return {
    initializeAuth,
    handleLoginSuccess,
    logout,
    handleUnauthorized,
  };
}
